// ── worldvectorlogo.com brand-logo API ───────────────────────────────────────
// Docs: https://worldvectorlogo.com/docs
//
// Anonymous access is rate-limited to 10 search/browse requests per day per IP.
// Since these calls run client-side, that limit is per *visitor*. The SVG CDN
// (cdn.worldvectorlogo.com) is unlimited and long-cached, so we render logos
// straight from `svg_url` and only fetch raw SVG markup on demand (copy/download).
//
// An API key is optional. If `REACT_APP_WVL_API_KEY` is set at build time it is
// appended to requests, lifting the quota to 500/day (free) or 5,000/day (pro).
// Nothing else changes — leave it unset to run fully anonymous.

const WVL_API = 'https://worldvectorlogo.com/api/v1';
const WVL_CDN = 'https://cdn.worldvectorlogo.com/logos';
const API_KEY = process.env.REACT_APP_WVL_API_KEY || '';
const CACHE_TTL = 24 * 60 * 60 * 1000; // 24h for list/search results
const DEFAULT_PER_PAGE = 48; // API hard-caps per_page at 50

// Synthetic "collection" so the sidebar can list Brand Logos like any Iconify set.
export const WVL_CATEGORY_ID = 'brand-logos';
export const WVL_CATEGORY = {
  id: WVL_CATEGORY_ID,
  prefix: WVL_CATEGORY_ID,
  name: 'Brand Logos',
  count: 0, // unknown total — hidden in the sidebar when 0
  iconifyCategory: 'Brands / Social',
  isLocal: false,
  isWvl: true,
};

// ── Cache helpers (mirror iconifyAPI.js conventions) ─────────────────────────
function cacheGet(key) {
  try {
    const raw = localStorage.getItem('ib-wvl-' + key);
    if (!raw) return null;
    const { data, ts } = JSON.parse(raw);
    return (Date.now() - ts > CACHE_TTL) ? null : data;
  } catch { return null; }
}

function cacheSet(key, data) {
  try {
    localStorage.setItem('ib-wvl-' + key, JSON.stringify({ data, ts: Date.now() }));
  } catch { /* storage full — skip */ }
}

function authParam() {
  return API_KEY ? `&api_key=${encodeURIComponent(API_KEY)}` : '';
}

// Map a worldvectorlogo API object → the app's icon shape.
// `svgContent` stays null until lazily fetched via fetchLogoSvg().
function mapLogo(logo) {
  const slug = logo.slug;
  const name = logo.name || slug;
  const tags = [...(logo.tags || []), name, slug, ...slug.split(/[-_]/)]
    .filter((t, i, a) => t && String(t).length > 1 && a.indexOf(t) === i)
    .map(String)
    .slice(0, 8);

  return {
    id: `wvl_${slug}`,
    slug,
    name,
    category: WVL_CATEGORY_ID,
    filename: `${slug}.svg`,
    svgUrl: logo.svg_url || `${WVL_CDN}/${slug}.svg`,
    svgContent: null, // populated on demand
    tags,
    downloads: logo.downloads || 0,
    featured: false,
    isWvl: true,
  };
}

// Attach inline SVG markup to each logo, fetched from the unlimited CDN.
// IMPORTANT: this CORS fetch must be the first request to each CDN URL — mixing
// a no-CORS <img> load first poisons CloudFront's cache and breaks the fetch, so
// logos are rendered from this inline markup rather than <img src>. Logos whose
// SVG can't be fetched (e.g. access-restricted brands → 403) are dropped.
async function hydrate(list) {
  const filled = await Promise.all(list.map(async (ic) => {
    if (ic.svgContent) return ic;
    const svg = await fetchLogoSvg(ic.svgUrl);
    return svg ? { ...ic, svgContent: svg } : null;
  }));
  return filled.filter(Boolean);
}

// ── Browse most-downloaded logos ─────────────────────────────────────────────
export async function browseLogos(perPage = DEFAULT_PER_PAGE, page = 1) {
  const limit = Math.min(perPage, 50);
  const key = `browse-${page}-${limit}`;
  let list = cacheGet(key); // metadata only — keeps the quota-limited list call cached

  if (!list) {
    try {
      const r = await fetch(
        `${WVL_API}/logos?per_page=${limit}&page=${page}&sort=downloads&order=desc${authParam()}`
      );
      if (!r.ok) return [];
      const data = await r.json();
      list = (data.data || []).map(mapLogo);
      if (list.length) cacheSet(key, list);
    } catch {
      return [];
    }
  }

  return hydrate(list);
}

// ── Search logos by company / brand name ─────────────────────────────────────
export async function searchLogos(query, perPage = DEFAULT_PER_PAGE, page = 1) {
  const q = (query || '').trim();
  if (!q) return [];
  const limit = Math.min(perPage, 50);
  const key = `search-${q.toLowerCase()}-${page}-${limit}`;
  let list = cacheGet(key); // metadata only

  if (!list) {
    try {
      const r = await fetch(
        `${WVL_API}/logos/search?q=${encodeURIComponent(q)}&per_page=${limit}&page=${page}${authParam()}`
      );
      if (!r.ok) return [];
      const data = await r.json();
      list = (data.data || []).map(mapLogo);
      if (list.length) cacheSet(key, list);
    } catch {
      return [];
    }
  }

  return hydrate(list);
}

// ── Fetch raw SVG markup for a single logo (CDN — unlimited, long-cached) ─────
// Returns a clean <svg>…</svg> string, or null on failure.
const _svgMem = new Map();
export async function fetchLogoSvg(svgUrl) {
  if (!svgUrl) return null;
  if (_svgMem.has(svgUrl)) return _svgMem.get(svgUrl);

  const lsKey = 'ib-wvlsvg-' + svgUrl;
  try {
    const cached = localStorage.getItem(lsKey);
    if (cached) { _svgMem.set(svgUrl, cached); return cached; }
  } catch { /* ignore */ }

  try {
    const r = await fetch(svgUrl);
    if (!r.ok) return null;
    let svg = await r.text();
    // Strip XML prolog / doctype — keep just the <svg>…</svg> element.
    const match = svg.match(/<svg[\s\S]*<\/svg>/i);
    svg = (match ? match[0] : svg).trim();
    _svgMem.set(svgUrl, svg);
    try { localStorage.setItem(lsKey, svg); } catch { /* storage full */ }
    return svg;
  } catch {
    return null;
  }
}
